/**
 * Session state.
 *
 * Only what the UI needs to decide what to render: whether we are signed in,
 * who we are, and which wallets are currently unlocked. No secret ever lands
 * here — the vault password is read from a field, sent, and dropped.
 */

import { create } from 'zustand';
import { gauntlet, tokenStore } from '../lib/api';

interface SessionState {
  status: 'loading' | 'unauthenticated' | 'authenticated' | 'setup-required';
  username: string | null;
  userId: number | null;
  totpEnabled: boolean;
  /** Mirrors what the server said. Hiding UI only — the server enforces. */
  isAdmin: boolean;
  unlockedWallets: number[];

  bootstrap: () => Promise<void>;
  signIn: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUnlocked: (walletIds: number[]) => void;
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  username: null,
  userId: null,
  totpEnabled: false,
  isAdmin: false,
  unlockedWallets: [],

  /** Work out, on load, whether to show setup, login, or the app. */
  async bootstrap() {
    try {
      const status = await gauntlet.authStatus();
      if (!status.setupComplete) {
        set({ status: 'setup-required' });
        return;
      }

      if (!tokenStore.get()) {
        set({ status: 'unauthenticated' });
        return;
      }

      const me = await gauntlet.me();
      set({
        status: 'authenticated',
        username: me.user.username,
        userId: me.user.id,
        totpEnabled: me.user.totp_enabled === 1,
        isAdmin: me.isAdmin,
        unlockedWallets: me.unlockedWallets,
      });
    } catch {
      // A rejected or expired token lands here; the client has already cleared it.
      tokenStore.clear();
      set({ status: 'unauthenticated', username: null, userId: null, isAdmin: false });
    }
  },

  async signIn(token: string) {
    tokenStore.set(token);
    await get().bootstrap();
  },

  async signOut() {
    try {
      await gauntlet.logout();
    } catch {
      // Even if the server call fails, drop the local token — the user asked
      // to be signed out and the UI must honour that.
    }
    tokenStore.clear();
    set({ status: 'unauthenticated', username: null, userId: null, unlockedWallets: [] });
  },

  setUnlocked(walletIds) {
    set({ unlockedWallets: walletIds });
  },
}));
